/**
 * Per-agent Ops Score dashboard (skill ring + trend chart).
 */
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DailySummaryItem } from '../../hooks/useMemorySummary';
import LoadingSpinner from '../shared/LoadingSpinner';
import {
  COMPETENCY_AXIS_COLORS,
  COMPETENCY_AXIS_ORDER,
  type CompetencyAxisId,
  computeAgentCompetencyMetrics,
} from './agentCompetencyMetrics';

type AgentCompetencyDashboardProps = {
  agentId: string;
  dailyItems?: DailySummaryItem[];
  kpiScore?: number | null;
  loading?: boolean;
  compact?: boolean;
};

function SkillArcRing({
  centerScore,
  axes,
  size = 140,
  scoreShortLabel,
}: {
  centerScore: number;
  axes: Record<CompetencyAxisId, number>;
  size?: number;
  scoreShortLabel?: string;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const stroke = size < 80 ? 7 : 10;
  const r = size / 2 - stroke - 4;
  const segments = COMPETENCY_AXIS_ORDER.map((id, i) => {
    const startAngle = -90 + i * 90;
    const sweep = (axes[id] / 100) * 85;
    return { id, startAngle, sweep, color: COMPETENCY_AXIS_COLORS[id] };
  });

  const polar = (angleDeg: number, radius: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
    };
  };

  const arcPath = (startDeg: number, endDeg: number) => {
    const start = polar(startDeg, r);
    const end = polar(endDeg, r);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" aria-hidden>
      {segments.map((seg) => (
        <path
          key={seg.id}
          d={arcPath(seg.startAngle, seg.startAngle + seg.sweep)}
          fill="none"
          stroke={seg.color}
          strokeWidth={stroke}
          strokeLinecap="round"
          opacity={0.92}
        />
      ))}
      <text
        x={cx}
        y={cy - (size < 80 ? 2 : 4)}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="currentColor"
        className="text-app-text"
        style={{ fontSize: size * 0.26, fontWeight: 700 }}
      >
        {centerScore}
      </text>
      {size >= 80 ? (
        <text
          x={cx}
          y={cy + size * 0.14}
          textAnchor="middle"
          fill="currentColor"
          className="text-app-text-dim"
          style={{ fontSize: size * 0.09, fontWeight: 600 }}
        >
          {scoreShortLabel}
        </text>
      ) : null}
    </svg>
  );
}

function AxisMiniBars({
  axes,
  axisLabel,
}: {
  axes: Record<CompetencyAxisId, number>;
  axisLabel: (id: CompetencyAxisId) => string;
}) {
  return (
    <ul className="mt-2 space-y-1">
      {COMPETENCY_AXIS_ORDER.map((id) => (
        <li key={id} className="flex items-center gap-2 text-[10px]">
          <span className="w-14 shrink-0 truncate text-app-text-dim" title={axisLabel(id)}>
            {axisLabel(id)}
          </span>
          <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-app-border/60">
            <div
              className="h-full rounded-full"
              style={{ width: `${axes[id]}%`, backgroundColor: COMPETENCY_AXIS_COLORS[id] }}
            />
          </div>
          <span className="w-6 shrink-0 text-right tabular-nums font-medium text-app-text">
            {axes[id]}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function AgentCompetencyDashboard({
  agentId,
  dailyItems = [],
  kpiScore = null,
  loading = false,
  compact = false,
}: AgentCompetencyDashboardProps) {
  const { t } = useTranslation();
  const gradientId = useId().replace(/:/g, '');

  const axisLabel = (id: CompetencyAxisId) =>
    t(`agents.competency.axes.${id}`, { defaultValue: id.toUpperCase() });

  const metrics = useMemo(
    () => computeAgentCompetencyMetrics(agentId, dailyItems, kpiScore),
    [agentId, dailyItems, kpiScore],
  );

  const stageLabel = t(`agents.competency.stages.${metrics.stage}`, {
    defaultValue: metrics.stage,
  });

  const scoreShortLabel = t('agents.competency.scoreShort', { defaultValue: 'Ops' });

  const sourceLabel = metrics.fromMemorySummaries
    ? t('agents.competency.sourceMemory', { defaultValue: 'จากสรุปความจำรายวัน' })
    : t('agents.competency.sourceEstimate', { defaultValue: 'ประมาณจาก KPI/โปรไฟล์' });

  if (loading && !dailyItems.length) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl border border-app-border bg-app-surface/80 ${
          compact ? 'min-h-[100px]' : 'min-h-[200px]'
        }`}
      >
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (compact) {
    return (
      <div className="min-w-0">
        <div className="flex items-start gap-3">
          <SkillArcRing centerScore={metrics.score} axes={metrics.axes} size={72} scoreShortLabel={scoreShortLabel} />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold text-app-text">
              {t('agents.competency.title', { defaultValue: 'คะแนนปฏิบัติการ (Ops Score)' })}
            </p>
            <p className="mt-0.5 text-[10px] text-app-text-dim leading-snug">
              {t('agents.competency.compactExplain', {
                defaultValue: 'คะแนน 0–100 จาก 4 มิติผลงาน (แก้ปัญหา · สม่ำเสมอ · ครบถ้วน · ลึก)',
              })}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                  metrics.scoreDelta >= 0
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
                }`}
              >
                {t('agents.competency.delta7d', {
                  defaultValue: '7 วัน',
                })}{' '}
                {metrics.scoreDelta >= 0 ? '+' : ''}
                {metrics.scoreDelta}
              </span>
              <span className="rounded bg-app-muted/50 px-1.5 py-0.5 text-[10px] text-app-text-dim">
                {stageLabel}
              </span>
            </div>
            <p className="mt-1 text-[9px] text-app-text-dim">{sourceLabel}</p>
          </div>
        </div>
        <AxisMiniBars axes={metrics.axes} axisLabel={axisLabel} />
      </div>
    );
  }

  return (
    <section className="min-w-0">
      <div className="mb-2">
        <p className="text-xs font-semibold text-app-text">
          {t('agents.competency.title', { defaultValue: 'คะแนนปฏิบัติการ (Ops Score)' })}
        </p>
        <p className="mt-0.5 text-[11px] text-app-text-dim leading-snug">
          {t('agents.competency.fullExplain', {
            defaultValue:
              'วัดจากสรุปความจำรายวัน: แก้ปัญหา · ความสม่ำเสมอ · ความครบถ้วน · ความลึก',
          })}
        </p>
        <p className="mt-1 text-[10px] text-app-accent/90">{sourceLabel}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-app-border/60 bg-app-bg/40 p-4 text-app-text">
          <SkillArcRing centerScore={metrics.score} axes={metrics.axes} size={160} scoreShortLabel={scoreShortLabel} />
          <div className="mt-3 flex flex-col items-center gap-1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-app-border bg-app-muted/40 px-2.5 py-0.5 text-[10px] font-semibold text-app-text">
              <span className="h-1.5 w-1.5 rounded-full bg-app-accent" />
              {stageLabel}
            </span>
            <p className="text-[11px] text-app-text-dim">
              {t('agents.competency.nextStage', { defaultValue: 'เป้าหมายถัดไป' })}{' '}
              <span className="font-semibold tabular-nums text-app-text">{metrics.stageTarget}</span>
            </p>
          </div>
          <AxisMiniBars axes={metrics.axes} axisLabel={axisLabel} />
        </div>

        <div className="rounded-xl border border-app-border/60 bg-app-bg/40 p-4 flex flex-col min-h-[200px]">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
                {t('agents.competency.scoreTrend', { defaultValue: 'แนวโน้ม Ops Score · 30 วัน' })}
              </p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold tabular-nums text-app-text">
                  {metrics.score.toFixed(0)}
                </span>
                <span
                  className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                    metrics.scoreDelta >= 0
                      ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                      : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
                  }`}
                >
                  {metrics.scoreDelta >= 0 ? '↑' : '↓'} {Math.abs(metrics.scoreDelta)}
                </span>
              </div>
            </div>
            <div className="text-right text-[9px] uppercase tracking-wide text-app-text-dim">
              <p>
                {t('agents.competency.peak', { defaultValue: 'สูงสุด' })}{' '}
                <span className="font-semibold text-app-text tabular-nums">{metrics.peak}</span>
              </p>
              <p>
                {t('agents.competency.trough', { defaultValue: 'ต่ำสุด' })}{' '}
                <span className="font-semibold text-app-text tabular-nums">{metrics.trough}</span>
              </p>
            </div>
          </div>
          <div className="flex-1 min-h-[120px]">
            <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height="100%">
              <AreaChart data={metrics.trend} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(var(--color-app-accent))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="rgb(var(--color-app-accent))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgb(var(--color-app-border))"
                  strokeOpacity={0.35}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9, fill: 'rgb(var(--color-app-text-dim))' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={[50, 100]}
                  tick={{ fontSize: 9, fill: 'rgb(var(--color-app-text-dim))' }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgb(var(--color-app-surface))',
                    border: '1px solid rgb(var(--color-app-border))',
                    borderRadius: 8,
                    fontSize: 11,
                  }}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullDate || ''}
                  formatter={(value) => [
                    value,
                    t('agents.competency.scoreShort', { defaultValue: 'Ops Score' }),
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="rgb(var(--color-app-accent))"
                  fill={`url(#${gradientId})`}
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
}
